import { jwtDecode } from 'jwt-decode';
import { loadUserPrefs, saveUserPrefs } from './utils/userPrefs';

const wsProto = window.location.protocol === "https:" ? "wss" : "ws";

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'no', label: 'Norwegian' },
  { code: 'de', label: 'German' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'ro', label: 'Romanian' },
];

export const urlMap = {
  // Profile
  profile: `/api/v1/core/users/me`,
  mySubscribers: (limit = 100, offset = 0) => `/api/v1/core/users/me/subscribers?limit=${limit}&offset=${offset}`,  
  myPublisherRoleRequest: `/api/v1/core/users/me/publisher-role-request`,
  publicAuthorFeed: (authorSlug, page = 1, pageSize = 10) => `/api/v1/core/users/public/${encodeURIComponent(authorSlug)}/feed?page=${page}&pageSize=${pageSize}`,
  publicAuthorReflection: (authorSlug, reflectionId) => `/api/v1/core/users/public/${encodeURIComponent(authorSlug)}/feed/reflections/${encodeURIComponent(reflectionId)}`,
  subscribeAuthorList: (authorSlug) => `/api/v1/core/users/public/${encodeURIComponent(authorSlug)}/subscribe`,

  // Auth
  login: '/api/v1/core/auth/login',
  register: '/api/v1/core/auth/register',
  recover: '/api/v1/core/auth/recover',
  verify: '/api/v1/core/auth/verify',
  adminVerify: '/api/v1/core/auth/admin-verify',
  adminBackup: '/api/v1/core/admin/backup',
  adminBackupList: (page = 1, pageSize = 10) => `/api/v1/core/admin/backup?page=${encodeURIComponent(page)}&pageSize=${encodeURIComponent(pageSize)}`,
  adminBackupDownload: (key) => `/api/v1/core/admin/backup/download?key=${encodeURIComponent(key)}`,
  adminPublisherRoleRequests: '/api/v1/core/admin/publisher-role-requests',
  approvePublisherRoleRequest: (requestId) => `/api/v1/core/admin/publisher-role-requests/${encodeURIComponent(requestId)}/approve`,
  adminReviewScaffolds: '/api/v1/core/admin/story-scaffolds/review',
  adminReviewScaffoldDetail: (scaffoldId, language) => `/api/v1/core/admin/story-scaffolds/${encodeURIComponent(scaffoldId)}${language ? `?language=${encodeURIComponent(language)}` : ''}`,
  adminApproveScaffold: (scaffoldId) => `/api/v1/core/admin/story-scaffolds/${encodeURIComponent(scaffoldId)}/approve`,
  adminSendBackScaffold: (scaffoldId) => `/api/v1/core/admin/story-scaffolds/${encodeURIComponent(scaffoldId)}/send-back`,
  adminJourneyOverview: (days = 30) => `/api/v1/core/admin/journey/overview?days=${encodeURIComponent(days)}`,
  resendVerification: '/api/v1/core/auth/resend-verification',
  requestAdminVerification: '/api/v1/core/auth/request-admin-verification',
  resetPassword: '/api/v1/core/auth/reset-password',

  // Newsletter (public, no auth)
  subscribeNewsletter: '/api/v1/core/newsletter/subscribe',
  unsubscribeNewsletter: '/api/v1/core/newsletter/unsubscribe',

  // Newsletter Ops (admin via worker)
  workerNewsletterGenerate: '/api/v1/worker/newsletter/generate',
  workerNewsletterSubscribers: '/api/v1/worker/newsletter/subscribers',
  workerNewsletterDrafts: '/api/v1/worker/newsletter/drafts',
  workerNewsletterDraft: (draftId) => `/api/v1/worker/newsletter/drafts/${encodeURIComponent(draftId)}`,
  workerNewsletterDeleteDraft: (draftId) => `/api/v1/worker/newsletter/drafts/${encodeURIComponent(draftId)}`,
  workerNewsletterPreviewTemplate: '/api/v1/worker/newsletter/preview-template',
  workerNewsletterSendNow: (draftId) => `/api/v1/worker/newsletter/drafts/${encodeURIComponent(draftId)}/send-now`,
  workerNewsletterSchedule: (draftId) => `/api/v1/worker/newsletter/drafts/${encodeURIComponent(draftId)}/schedule`,
  workerNewsletterUnschedule: (draftId) => `/api/v1/worker/newsletter/drafts/${encodeURIComponent(draftId)}/unschedule`,

  // Publisher newsletter panel (core publisher routes)
  workerPublisherNewsletterGenerate: '/api/v1/worker/newsletter/generate',
  workerPublisherNewsletterSubscribers: '/api/v1/core/publisher/newsletter/subscribers',
  workerPublisherNewsletterDrafts: '/api/v1/core/publisher/newsletter/drafts',
  workerPublisherNewsletterDraft: (draftId) => `/api/v1/core/publisher/newsletter/drafts/${encodeURIComponent(draftId)}`,
  workerPublisherNewsletterDeleteDraft: (draftId) => `/api/v1/core/publisher/newsletter/drafts/${encodeURIComponent(draftId)}`,
  workerPublisherNewsletterPreviewTemplate: '/api/v1/core/publisher/newsletter/preview-template',
  workerPublisherNewsletterSendNow: (draftId) => `/api/v1/core/publisher/newsletter/drafts/${encodeURIComponent(draftId)}/send-now`,
  workerPublisherNewsletterSchedule: (draftId) => `/api/v1/core/publisher/newsletter/drafts/${encodeURIComponent(draftId)}/schedule`,
  workerPublisherNewsletterUnschedule: (draftId) => `/api/v1/core/publisher/newsletter/drafts/${encodeURIComponent(draftId)}/unschedule`,

  // Contact form (public, no auth)
  contact: '/api/v1/core/contact',

  // Projects & Chapters
  listProjects: '/api/v1/core/projects',
  listDrafts: '/api/v1/core/drafts',
  createDraft: '/api/v1/core/drafts',
  getDraft: (draftId) => `/api/v1/core/drafts/${draftId}`,
  updateDraft: (draftId) => `/api/v1/core/drafts/${draftId}`,
  deleteDraft: (draftId) => `/api/v1/core/drafts/${draftId}`,
  exportDraftPdf: (draftId) => `/api/v1/core/drafts/${draftId}/export/pdf`,

  // Writer sessions (Pomodoro)
  createWriterSession: '/api/v1/core/writer-sessions',
  updateWriterSession: (sessionId) => `/api/v1/core/writer-sessions/${sessionId}`,
  deleteWriterSession: (sessionId) => `/api/v1/core/writer-sessions/${sessionId}`,
  writerSessionsCount: (chapterId) => `/api/v1/core/writer-sessions/count?chapterId=${chapterId}`,
  listWriterSessions: (chapterId) => `/api/v1/core/writer-sessions?chapterId=${chapterId}`,
  listWriterSessionReflections: (sessionId) => `/api/v1/core/writer-sessions/${sessionId}/reflections`,
  writerSessionReflection: (reflectionId) => `/api/v1/core/writer-sessions/reflections/${encodeURIComponent(reflectionId)}`,
  listChapterReflectionProgress: (chapterId) => `/api/v1/core/reflections/${chapterId}/progress`,
  updateWriterSessionReflection: (sessionId, reflectionId) => `/api/v1/core/writer-sessions/${sessionId}/reflections/${reflectionId}`,
  updateReflectionStatus: (sessionId, reflectionId) => `/api/v1/core/writer-sessions/${sessionId}/reflections/${reflectionId}/status`,
  projectEndpoint: '/api/v1/core/projects',
  getProject: '/api/v1/core/projects/:projectId',
  projectProgressFeed: (projectId, page = 1, pageSize = 25) => `/api/v1/core/projects/${encodeURIComponent(projectId)}/progress-feed?page=${page}&pageSize=${pageSize}`,
  projectHistory: (projectId) => `/api/v1/core/projects/${encodeURIComponent(projectId)}/history`,
  exportProjectProofOfWorkPdf: (projectId) => `/api/v1/core/projects/${encodeURIComponent(projectId)}/proof-of-work-pdf`,
  listProjectTypes: '/api/v1/core/project-types',
  listChapters: (projectId) => `/api/v1/core/projects/${projectId}/chapters`,
  searchProjectChapters: (projectId, query, mode = 'contains', limit = 100) => {
    const params = new URLSearchParams();
    params.set('q', query || '');
    params.set('mode', mode || 'contains');
    params.set('limit', String(limit || 100));
    return `/api/v1/core/projects/${projectId}/chapters/search?${params.toString()}`;
  },
  getProjectClipboard: (projectId) => `/api/v1/core/projects/${projectId}/clipboard`,
  getProjectIssues: (projectId) => `/api/v1/core/issues/project/${projectId}`,
  createProjectIssue: (projectId) => `/api/v1/core/issues/project/${projectId}`,
  updateProjectIssue: (_projectId, issueId) => `/api/v1/core/issues/${issueId}`,
  deleteProjectIssue: (_projectId, issueId) => `/api/v1/core/issues/${issueId}`,
  purgeProjectIssues: (projectId) => `/api/v1/core/issues/project/${projectId}`,
  updateProjectIssueStatus: (_projectId, issueId) => `/api/v1/core/issues/${issueId}/status`,
  reassignIssue: (_projectId, issueId) => `/api/v1/core/issues/${issueId}/reassign`,

  // Agent execution
  executeAgent: (projectId) => `/api/v1/core/agent/${projectId}/execute`,
  pauseAgent: (executionId) => `/api/v1/core/agent/executions/${executionId}/pause`,
  cancelAgent: (executionId) => `/api/v1/core/agent/executions/${executionId}/cancel`,
  continueAgent: (executionId) => `/api/v1/core/agent/executions/${executionId}/continue`,
  resumeAgent: (executionId) => `/api/v1/core/agent/executions/${executionId}/resume`,
  getAgentExecution: (executionId) => `/api/v1/core/agent/executions/${executionId}`,
  getLatestAgentExecution: (projectId) => `/api/v1/core/agent/projects/${projectId}/latest-execution`,
  getAgentConfig: (projectId) => `/api/v1/core/agent/${projectId}/config`,
  getAgentMenu: (stage) => stage ? `/api/v1/core/agent/menu?stage=${stage}` : `/api/v1/core/agent/menu`,
  getAgentLogs: (executionId) => `/api/v1/core/agent/executions/${executionId}/logs`,
  runAgentStep: (projectId, stepName) => `/api/v1/core/agent/${projectId}/steps/${stepName}`,
  executeChapterAgent: (projectId, chapterId) => `/api/v1/core/agent/${projectId}/chapters/${chapterId}/execute`,
  getStepResults: (projectId, stepName) => `/api/v1/core/agent/${projectId}/results/${stepName}`,

  // Project-level issues (structural & thematic)
  getStructuralThematicIssues: (projectId) => `/api/v1/core/projects/${projectId}/project-issues`,
  updateStructuralThematicIssueStatus: (projectId, issueId) => `/api/v1/core/projects/${projectId}/project-issues/${issueId}/status`,
  updateStructuralThematicIssue: (projectId, issueId) => `/api/v1/core/projects/${projectId}/project-issues/${issueId}`,
  deleteStructuralThematicIssue: (projectId, issueId) => `/api/v1/core/projects/${projectId}/project-issues/${issueId}`,
  deleteAllStructuralThematicIssues: (projectId) => `/api/v1/core/projects/${projectId}/project-issues`,
  moveStructuralIssueToChapter: (projectId, issueId) => `/api/v1/core/projects/${projectId}/project-issues/${issueId}/move-to-chapter`,

  // Wallet / Fig Coins
  walletBalance:        '/api/v1/core/wallet/balance',
  walletPricing:        '/api/v1/core/wallet/pricing',
  walletPurchaseIntent: '/api/v1/core/wallet/purchase/intent',
  walletPurchaseConfirm: '/api/v1/core/wallet/purchase/confirm',
  walletTransactions:   '/api/v1/core/wallet/transactions',
  walletSubscriptionCheckout: '/api/v1/core/wallet/subscription/checkout',
  walletSubscriptionStatus: '/api/v1/core/wallet/subscription/status',

  // Account management
  accountFarewellQuote:   '/api/v1/core/account/farewell-quote',
  accountDeletionRequest: '/api/v1/core/account/deletion-request',
  accountDeletionConfirm: '/api/v1/core/account/deletion-confirm',

  // Social Media
  socialHealthCheck: '/worker',
  socialChatMessages: '/api/v1/worker/chat/messages',
  socialProfile: '/api/v1/worker/profile',
  socialConnections: '/api/v1/worker/connections',
  socialConnectInstagram: '/api/v1/worker/connections/instagram/connect',
  socialInstagramStart: '/api/v1/worker/connections/instagram/start',
  socialInstagramMedia: '/api/v1/worker/connections/instagram/media',

  // Onboarding (no-auth GUID draft bootstrap)
  bootstrapOnboardingDraft: (guid, sourceProjectId, sourceChapterId) => {
    const params = new URLSearchParams();
    if (sourceProjectId) params.set('sourceProjectId', sourceProjectId);
    if (sourceChapterId) params.set('sourceChapterId', sourceChapterId);
    const qs = params.toString();
    return `/api/v1/core/onboarding-drafts/${guid}/bootstrap${qs ? `?${qs}` : ''}`;
  },
  updateOnboardingDraft: (guid) => `/api/v1/core/onboarding-drafts/${guid}`,
  onboardingChat: (guid) => `/api/v1/core/onboarding-drafts/${guid}/chat`,
  shareDraft: (guid) => `/api/v1/core/onboarding-drafts/${guid}/share`,
  submitOnboardingDraft: (guid) => `/api/v1/core/onboarding-drafts/${guid}/submit`,

  // Draft submissions inbox (owner)
  draftInbox: '/api/v1/core/drafts/inbox',
  draftInboxByChapter: '/api/v1/core/drafts/inbox/by-chapter',
  draftInboxForChapter: (chapterId) => `/api/v1/core/drafts/inbox/chapter/${chapterId}`,
  draftReview: (draftId) => `/api/v1/core/drafts/${draftId}/review`,

  // Shareable Chapters
  shareChapter: (projectId, chapterId) => `/api/v1/core/chapters/${projectId}/${chapterId}/share`,
  bootstrapShareableChapter: (guid) => `/api/v1/core/chapters/share/${guid}/bootstrap`,
  shareableChapterWriter: (guid) => `/draft/share/${guid}`,
  shareableDraftWriter: (guid) => `/draft/${guid}/writer`,

  // Plot
  getProjectPlot: (projectId) => `/api/v1/core/agent/${projectId}/results/GeneratePlotStep`,
  
  // Characters
  getProjectCharacters: (projectId) => `/api/v1/core/characters/${projectId}`,
  getProjectCharactersWithAppearances: (projectId) => `/api/v1/core/characters/${projectId}/with-appearances`,
  getCharacter: (characterId) => `/api/v1/core/characters/character/${characterId}`,
  deleteProjectCharacters: (projectId) => `/api/v1/core/characters/${projectId}`,
  
  // Summarization (legacy - kept for backward compatibility)
  summarizeProject: '/api/v1/core/projects/:projectId/summarize',
  getProjectSummaries: (projectId) => `/api/v1/core/projects/${projectId}/summaries`,
  regenerateChapterSummary: (chapterId) => `/api/v1/core/chapters/${chapterId}/summaries`,
  updateChapterSummary: (chapterId, summaryId) => `/api/v1/core/chapters/${chapterId}/summaries/${summaryId}`,
  getChapterSummary: (chapterId) => `/api/v1/core/chapters/${chapterId}/summaries`,
  deleteChapterSummary: (chapterId) => `/api/v1/core/chapters/${chapterId}/summaries`,
  
  // Scenes
  getChapterScenes: (projectId) => `/api/v1/core/scenes/project/${projectId}/counts`,
  getScenesForChapter: (chapterId) => `/api/v1/core/scenes/${chapterId}`,
  regenerateChapterScenes: (chapterId) => `/api/v1/core/scenes/${chapterId}`,

  chapterEndpoint: '/api/v1/core/chapters',
  archiveChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}/archive`,
  restoreChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}/restore`,
  listChapterStatuses: '/api/v1/core/chapters/meta/statuses',
  updateChapterStatus: (chapterId) => `/api/v1/core/chapters/${chapterId}/status`,
  deleteChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}`,

  getChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}`,
  exportChapterPdf: (chapterId) => `/api/v1/core/chapters/${chapterId}/export/pdf`,
  updateMetadataItemStatus: (chapterId) => `/api/v1/core/chapters/${chapterId}/metadata`,
  createChapterHole: (chapterId) => `/api/v1/core/chapters/${chapterId}/metadata/holes`,
  getChapterIssues: (chapterId) => `/api/v1/core/issues/chapter/${chapterId}`,
  updateChapterIssue: (_chapterId, issueId) => `/api/v1/core/issues/${issueId}`,
  deleteChapterIssue: (_chapterId, issueId) => `/api/v1/core/issues/${issueId}`,
  reassignChapterIssue: (_chapterId, issueId) => `/api/v1/core/issues/${issueId}/reassign`,
  updateChapterIssueStatus: (_chapterId, issueId) => `/api/v1/core/issues/${issueId}/status`,
  putChapterAct: (chapterId) => `/api/v1/core/chapters/${chapterId}/act`,
  updateChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}`,
  getChapterHistory: (chapterId) => `/api/v1/core/chapters/${chapterId}/history`,
  getChapterSnapshot: (chapterId, commitHash) => `/api/v1/core/chapters/${chapterId}/history/${encodeURIComponent(commitHash)}`,
  commitChapter: (chapterId) => `/api/v1/core/chapters/${chapterId}/commit`,
  approveSuggestion: (projectId, issueId) => `/api/v1/core/projects/${projectId}/suggestions/${issueId}/approve`,
  dismissSuggestion: (projectId, issueId) => `/api/v1/core/projects/${projectId}/suggestions/${issueId}/dismiss`,

  // Video Upload
  initMultiPartUpload: '/api/v1/worker/video/upload/multipart/init',
  completeMultiPartUpload: '/api/v1/worker/video/upload/multipart/complete',
  uploadMultiPartUrl: '/api/v1/worker/video/upload/multipart/part-url',
  abortMultiPartUpload: '/api/v1/worker/video/upload/multipart/abort',

  // Videos
  listVideos: '/api/v1/worker/video/list',
  getVideoById: '/api/v1/worker/video/:id',
  listChapterVideos: (chapterId) => `/api/v1/worker/video/chapter/${chapterId}`,

  // Splits
  lookupSplitList: '/api/v1/worker/video_chunk/split25/lookup',
  deleteSplit: '/api/v1/worker/video_chunk/split25/delete',

  // Engine (video + story)
  split25: '/api/v1/engine/video/split25',
  decorateChunk: '/api/v1/engine/video/split25/decorate',
  split25Start: '/api/v1/engine/video/split25/start',
  split25Status: '/api/v1/engine/video/split25/status/',
  split25Result: '/api/v1/engine/video/split25/result/',
  split25Cancel: '/api/v1/engine/video/split25/cancel',

  notificationsWs: () =>
  `${wsProto}://${window.location.host}/api/v1/engine/video/split25/ws`,


  // ------------------
  // Chat
  // ------------------
  
  listLlms: () => `/api/v1/chat/llms`,
  coachModel: () => `/api/v1/chat/llms/coach`,
  listChatSessions: (chapterId) => chapterId ? `/api/v1/chat/${chapterId}/sessions` : `/api/v1/chat/sessions`,  
  listProjectChatSessions: (projectId) => `/api/v1/core/projects/${projectId}/coach/sessions`,
  createProjectChatSession: (projectId) => `/api/v1/core/projects/${projectId}/coach/sessions`,
  createProjectChatMessage: (projectId) => `/api/v1/core/projects/${projectId}/coach/messages`,
  createChatSession: '/api/v1/chat/sessions',
  createDraftChatSession: '/api/v1/chat/drafts',
  getChatSession: (sessionId) => `/api/v1/chat/sessions/${sessionId}`,
  updateChatSession: (sessionId) => `/api/v1/chat/sessions/${sessionId}`,
  deleteChatSession: (sessionId) => `/api/v1/chat/sessions/${sessionId}`,

  // Messages
  listChatMessages: () =>
    `/api/v1/chat/messages`,
  createChatMessage: () =>
    `/api/v1/chat/messages`,
  createIssueChatMessage: () =>
    `/api/v1/chat/issue-messages`,
  streamIssueMessage: (runId) =>
    `/api/v1/chat/issue-messages/${runId}/stream`,

  // Pomodoro Reflection
  startReflection: () => `/api/v1/chat/start-reflection`,
  startReflectionEnhanced: () => `/api/v1/chat/start-reflection-enhanced`,
  reflectionMessages: () => `/api/v1/chat/reflection-messages`,
  reflectionMessagesEnhanced: () => `/api/v1/chat/reflection-messages-enhanced`,

  // Short story questionnaire chat
  listStoryScaffolds: (language) => `/api/v1/core/story-scaffolds${language ? `?language=${encodeURIComponent(language)}` : ''}`,
  listAuthorStoryScaffolds: (language) => `/api/v1/core/story-scaffolds/author${language ? `?language=${encodeURIComponent(language)}` : ''}`,
  getAuthorStoryScaffold: (scaffoldId, language) => `/api/v1/core/story-scaffolds/author/${encodeURIComponent(scaffoldId)}${language ? `?language=${encodeURIComponent(language)}` : ''}`,
  createStoryScaffold: () => `/api/v1/core/story-scaffolds`,
  deleteStoryScaffold: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}`,
  updateStoryScaffold: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}`,
  updateStoryScaffoldSystemPrompt: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}/system-prompt`,
  publishStoryScaffold: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}/publish`,
  unpublishStoryScaffold: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}/unpublish`,
  submitStoryScaffoldReview: (scaffoldId) => `/api/v1/core/story-scaffolds/${encodeURIComponent(scaffoldId)}/submit-review`,
  startScaffoldSimulation: () => `/api/v1/core/scaffold-simulations/start`,
  listScaffoldSimulations: (scaffoldId, limit = 20) => `/api/v1/core/scaffold-simulations?storyScaffoldId=${encodeURIComponent(scaffoldId)}&limit=${encodeURIComponent(limit)}`,
  getScaffoldSimulation: (simulationId) => `/api/v1/core/scaffold-simulations/${encodeURIComponent(simulationId)}`,
  answerScaffoldSimulation: (simulationId) => `/api/v1/core/scaffold-simulations/${encodeURIComponent(simulationId)}/answer`,
  suggestScaffoldSimulationAnswer: (simulationId) => `/api/v1/core/scaffold-simulations/${encodeURIComponent(simulationId)}/suggest-answer`,
  deleteScaffoldSimulation: (simulationId) => `/api/v1/core/scaffold-simulations/${encodeURIComponent(simulationId)}`,
  generateScaffoldSimulation: (simulationId) => `/api/v1/core/scaffold-simulations/${encodeURIComponent(simulationId)}/generate`,
  shortStoryStart: () => `/api/v1/chat/short-story/start`,
  shortStoryAnswer: () => `/api/v1/chat/short-story/answer`,
  shortStoryStatus: () => `/api/v1/chat/short-story/status`,

  // Short story draft generation
  shortStoryGenerate: '/api/v1/core/short-story/generate-from-session',
  shortStoryStream: (generationId) =>
    `/api/v1/core/short-story/generate-from-session/stream?generationId=${encodeURIComponent(generationId)}`,

  // Issue Resolution Flows
  issueFlows: '/api/v1/core/issue-flows',
  getIssueFlow: (flowId) => `/api/v1/core/issue-flows/${flowId}`,
  getActiveIssueFlow: (issueId) => `/api/v1/core/issue-flows?issueId=${issueId}`,
  respondToFlowStep: (flowId, stepIndex) => `/api/v1/core/issue-flows/${flowId}/steps/${stepIndex}/respond`,
  verifyFlowStep: (flowId, stepIndex) => `/api/v1/core/issue-flows/${flowId}/steps/${stepIndex}/verify`,
  completeIssueFlow: (flowId) => `/api/v1/core/issue-flows/${flowId}/complete`,
  abandonIssueFlow: (flowId) => `/api/v1/core/issue-flows/${flowId}/abandon`,

  // Runs
  completeChatRun: (runId) => `/api/v1/chat/runs/${runId}/complete`,
  failChatRun: (runId) => `/api/v1/chat/runs/${runId}/fail`,
  appendChatRunChunk: (runId) => `/api/v1/chat/runs/${runId}/chunks`,
  listChatRunChunks: (runId) => `/api/v1/chat/runs/${runId}/chunks`,
  // e.g. in your urlMap
  streamRun: (runId) => `/api/v1/chat/runs/${runId}/stream`,
};


export const appNavigation = {
  contact: "/contact",
  recoverPassword: "/recover-password",
  resetPassword: "/reset-password",
  verify: "/verify",
  adminVerifyUser: "/admin/verify-user",
  adminPanel: "/admin/panel",
  publisherPanel: "/publisher/panel",
  onboarding: "/on-boarding",
  register: "/register",
  login: "/login",
  subscribe: "/subscribe",
  unsubscribe: "/unsubscribe",
  home: "/home",
  profile: "/profile",
  shortStoryChat: "/short-story/chat",
  shortStoryGenerating: "/short-story/generating",
  scaffoldEditor: (scaffoldId) => `/scaffolds/${encodeURIComponent(scaffoldId)}/edit`,
}

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getTokenRole = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?.role ?? null;
  } catch {
    return null;
  }
};

export const getTokenSubscriptionStatus = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const decoded = jwtDecode(token);
    const plan = String(decoded?.plan || '').toLowerCase();
    const planStatus = String(decoded?.planStatus || '').toLowerCase();

    if (!plan && !planStatus && typeof decoded?.isPro !== 'boolean') return null;

    const normalizedPlan = plan === 'premium' ? 'premium' : 'free';
    const normalizedStatus = planStatus || 'inactive';
    const isPro = typeof decoded?.isPro === 'boolean'
      ? decoded.isPro
      : normalizedPlan === 'premium' && (normalizedStatus === 'active' || normalizedStatus === 'trialing');

    return {
      plan: normalizedPlan,
      planStatus: normalizedStatus,
      isPro: !!isPro,
    };
  } catch {
    return null;
  }
};

export const getRoleViewOverride = () => {
  const prefs = loadUserPrefs();
  return prefs.effectiveRole === 'publisher' || prefs.effectiveRole === 'user'
    ? prefs.effectiveRole
    : null;
};

export const setRoleViewOverride = (role) => {
  if (role === 'publisher' || role === 'user') {
    saveUserPrefs({ effectiveRole: role });
    return;
  }
  saveUserPrefs({ effectiveRole: null });
};

export const getEffectiveRole = () => {
  const tokenRole = getTokenRole();
  if (tokenRole !== 'publisher' && tokenRole !== 'admin') {
    setRoleViewOverride(null);
    return tokenRole;
  }

  const override = getRoleViewOverride();
  if (tokenRole === 'publisher') {
    return override === 'user' ? 'user' : 'publisher';
  }

  return override === 'publisher' || override === 'user'
    ? override
    : 'admin';
};

// Removes HTML tags and decodes basic entities for plain text usage.
  // Mirrors the API's stripHtml used server-side to sanitize chapter content.
  export function stripHtml(input) {
    if (!input) return "";

    let text = String(input)
      // Replace <br> and certain closing block tags with newlines first
      .replace(/<br\s*\/?>(?!\n)/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<\/div>/gi, "\n")
      .replace(/<\/li>/gi, "\n");

    // Remove scripts, styles, and all remaining HTML tags
    text = text
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
      .replace(/<[^>]+>/g, "");

    // Decode common entities
    text = text
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    return text.replace(/\s+/g, " ").trim();
  }
// helper: flatten ContentPart[] to display text
export function partsToPlainText(parts) {
  if (!Array.isArray(parts)) return "";
  return parts.map((p) => (p && p.type === "text" && typeof p.text === "string" ? p.text : "")).join("");
}